var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Dashboard from "@core/dashboard/Dashboard";
import ErrorBoundary from "@helper/ErrorBoundary/ErrorBoundary";
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UXPContext } from "@core/context/Context";
import { hasValue } from "@utils";
import { saveDefaultUserPrefs, saveUserPrefs, savePortalConfiguration, mergeSettings } from "@core/services/services";
import useToast from "@notifications/Toast/useToast";
import { registerFloatingActionSection, unregisterFloatingActionSection, FloatingActionSectionType, FloatingActionButtonVisibility } from "@core/floating-actions/floatingActionsRegistry";
import { checkAndAutoConfigureWidget, isResponsiveLayoutConfiguration, DEFAULT_BREAKPOINTS } from "@core/dashboard/dashboardUtils";
import { lookupComponent, toJSON } from "@utils";
import DashboardSettingsModal from "@core/dashboard/configurations/DashboardSettingsModal";
import DashboardConfigPanel from "@core/dashboard/configurations/DashboardConfigPanel";
import { WidgetDrawerType } from "@core/dashboard/widget-drawer/WidgetDrawer";
import { useRouterContext, useSearchParams } from "@hooks/useRouteHandlers";
const DashboardViewBase = ({ details, widgetConfiguration, subDashboardId, globalProps, globalDashboardSettings }) => {
    const context = useContext(UXPContext);
    const toast = useToast();
    const { configuredashboard } = useSearchParams();
    const { navigate, generateURL } = useRouterContext();
    // For public dashboards (IsPublic='1'), swap in the public user credentials so that
    // widgets make service calls on behalf of the public user rather than the anonymous visitor.
    // Only applies when the visitor has no credentials of their own (public session).
    // Use the immutable isPublicSession flag — not reactive apiKey/userKey which change after setPublicCredentials.
    const isAnonymous = context.isPublicSession;
    // Redirect anonymous users away from private dashboards
    useEffect(() => {
        if (isAnonymous && details && details.IsPublic !== '1') {
            navigate('/view/auth/login');
        }
    }, [isAnonymous, details === null || details === void 0 ? void 0 : details.Key, details === null || details === void 0 ? void 0 : details.IsPublic]);
    useEffect(() => {
        if (isAnonymous && (details === null || details === void 0 ? void 0 : details.IsPublic) === '1' && hasValue(details === null || details === void 0 ? void 0 : details.PublicUserAPIKey)) {
            context.setPublicCredentials(details.PublicUserAPIKey, details.PublicUserKey || '');
        }
        return () => {
            if (isAnonymous) {
                // Always restore to empty — avoids leaking public credentials to a subsequent private dashboard
                context.setPublicCredentials('', '');
            }
        };
    }, [details === null || details === void 0 ? void 0 : details.Key, isAnonymous]);
    const initialState = useMemo(() => {
        const isResponsiveLayout = isResponsiveLayoutConfiguration(widgetConfiguration);
        // Get raw widgets from configuration
        let rawWidgets = [];
        let rawLayouts = {};
        if (isResponsiveLayout) {
            const config = widgetConfiguration;
            rawWidgets = (config === null || config === void 0 ? void 0 : config.widgets) || [];
            rawLayouts = (config === null || config === void 0 ? void 0 : config.layouts) || {};
        }
        else {
            const config = widgetConfiguration;
            rawWidgets = (config === null || config === void 0 ? void 0 : config.installed) || [];
            rawLayouts = {};
        }
        // Process widgets: match with registered widgets and apply auto-configuration
        const processedWidgets = rawWidgets.map(widget => {
            const registered = lookupComponent(widget.id);
            if (registered) {
                widget.component = registered.component;
                widget.configs = registered.configs;
                widget.isNotAvalable = false;
            }
            else {
                widget.isNotAvalable = true;
            }
            // Check and auto-configure if applicable
            return checkAndAutoConfigureWidget(widget);
        });
        return {
            widgets: processedWidgets,
            layouts: rawLayouts,
            gridSettings: (widgetConfiguration === null || widgetConfiguration === void 0 ? void 0 : widgetConfiguration.gridSettings) || null,
        };
    }, [widgetConfiguration]);
    const perDashboardSettings = useMemo(() => {
        if (!(details === null || details === void 0 ? void 0 : details.Configuration))
            return null;
        return toJSON(details.Configuration, null);
    }, [details === null || details === void 0 ? void 0 : details.Configuration]);
    const [widgets, setWidgets] = useState(initialState.widgets);
    const [layouts, setLayouts] = useState(initialState.layouts);
    const [isEditing, setIsEditing] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [gridSettings, setGridSettings] = useState(initialState.gridSettings);
    const [dashboardSpecificSettings, setDashboardSpecificSettings] = useState(perDashboardSettings);
    // Recomputed from gridSettings state so saves immediately update effective values.
    // Global defaults fill in values not explicitly set per-dashboard.
    const effectiveSettings = useMemo(() => mergeSettings(globalDashboardSettings || null, perDashboardSettings, gridSettings), [globalDashboardSettings, perDashboardSettings, gridSettings]);
    const [selectedBreakpoint, setSelectedBreakpoint] = useState(null);
    const effectiveBreakpoints = useMemo(() => {
        const activeBreakpointMap = new Map(effectiveSettings.activeBreakpoints.map(b => [b.key, b]));
        const base = (gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.columns)
            ? Object.fromEntries(Object.entries(DEFAULT_BREAKPOINTS).map(([k, v]) => [k, Object.assign(Object.assign({}, v), { columns: gridSettings.columns })]))
            : DEFAULT_BREAKPOINTS;
        return Object.fromEntries(Object.entries(base)
            .filter(([k]) => activeBreakpointMap.has(k))
            .map(([k, v]) => {
            const activeEntry = activeBreakpointMap.get(k);
            return [k, Object.assign(Object.assign({}, v), { width: activeEntry.width, columns: activeEntry.columns })];
        }));
    }, [gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.columns, effectiveSettings.activeBreakpoints]);
    const floatingActionsControlsRef = useRef(null);
    const dashboardContainerRef = useRef(null);
    useEffect(() => {
        setIsEditing(!isAnonymous && configuredashboard == '1');
    }, [configuredashboard, isAnonymous]);
    // Update local state when props change
    useEffect(() => {
        setWidgets(initialState.widgets);
        setLayouts(initialState.layouts);
        setGridSettings(initialState.gridSettings);
    }, [initialState]);
    const handleSave = useCallback((updatedWidgets, updatedLayouts) => __awaiter(void 0, void 0, void 0, function* () {
        const portalKey = details === null || details === void 0 ? void 0 : details.Key;
        if (!portalKey) {
            toast.error('Unable to save: Dashboard key not found');
            return false;
        }
        const configuration = {
            widgets: updatedWidgets,
            layouts: updatedLayouts,
            installed: updatedWidgets,
        };
        const success = yield saveUserPrefs(context, configuration, portalKey, subDashboardId);
        // Update local state after successful save
        if (success) {
            setWidgets(updatedWidgets);
            setLayouts(updatedLayouts);
        }
        return success;
    }), [context, details, subDashboardId]);
    const handleEnterEdit = useCallback(() => {
        navigate(generateURL({ searchParams: { 'configuredashboard': '1' } }));
    }, [context]);
    const handleExitEdit = useCallback(() => {
        // Simply exit edit mode - changes are already saved by Dashboard component
        toast.success(context.$L('uxp-core.text.changes-saved'));
        navigate(generateURL({ removeParams: ['configuredashboard'] }));
    }, [context]);
    const handleWidgetDrawerChanges = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        if (event.widgets && event.layouts) {
            return handleSave(event.widgets, event.layouts);
        }
        return true;
    }), [handleSave]);
    const handleSaveDefaultDashboard = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const portalKey = details === null || details === void 0 ? void 0 : details.Key;
        if (!portalKey) {
            toast.error('Unable to save: Dashboard key not found');
            return;
        }
        const configuration = {
            widgets: widgets || [],
            layouts: layouts || {},
            gridSettings: gridSettings || undefined,
            installed: widgets || [],
        };
        const success = yield saveDefaultUserPrefs(context, configuration, portalKey, subDashboardId);
        if (success) {
            toast.success(context.$L('uxp-core.admin.saved'));
        }
        else {
            toast.error(context.$L('uxp-core.text.unable-to-save'));
        }
    }), [widgets, layouts, gridSettings, context]);
    const handleClearWidgets = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const portalKey = details === null || details === void 0 ? void 0 : details.Key;
        if (!portalKey)
            return;
        const emptyConfiguration = {
            widgets: [],
            layouts: {},
            gridSettings: gridSettings || undefined,
            installed: [],
        };
        const success = yield saveUserPrefs(context, emptyConfiguration, portalKey, subDashboardId);
        if (success) {
            setWidgets([]);
            setLayouts({});
        }
    }), [context, details, subDashboardId, gridSettings]);
    const handleSaveDashboardSpecificSettings = useCallback((settings) => __awaiter(void 0, void 0, void 0, function* () {
        const portalKey = details === null || details === void 0 ? void 0 : details.Key;
        if (!portalKey)
            return;
        const success = yield savePortalConfiguration(context, portalKey, settings);
        if (success)
            setDashboardSpecificSettings(settings);
    }), [context, details]);
    const handleSaveGridSettings = useCallback((settings) => __awaiter(void 0, void 0, void 0, function* () {
        const portalKey = details === null || details === void 0 ? void 0 : details.Key;
        if (!portalKey)
            return;
        const configuration = {
            widgets: widgets || [],
            layouts: layouts || {},
            gridSettings: settings,
            installed: widgets || [],
        };
        const success = yield saveUserPrefs(context, configuration, portalKey, subDashboardId);
        if (success)
            setGridSettings(settings);
    }), [context, details, subDashboardId, widgets, layouts]);
    const handleBreakpointSelect = useCallback((breakpoint) => {
        setSelectedBreakpoint(breakpoint);
    }, []);
    const handleContainerMount = useCallback((ref) => {
        dashboardContainerRef.current = ref;
    }, []);
    const customActions = useMemo(() => [
        {
            icon: 'phl gear',
            onClick: () => setShowSettingsModal(prev => !prev),
            tooltip: context.$L('uxp-core.text.settings'),
            variant: 'secondary',
            visibility: FloatingActionButtonVisibility.Edit
        }
    ], [context]);
    // Register dashboard floating action section
    useEffect(() => {
        var _a, _b;
        registerFloatingActionSection({
            id: 'dashboard',
            type: FloatingActionSectionType.Dashboard,
            widgets: widgets,
            layouts: layouts,
            enableEdit: !isAnonymous,
            isEditing: isEditing,
            onEnterEditMode: handleEnterEdit,
            onExitEditMode: handleExitEdit,
            enableAdd: !isAnonymous,
            addButtonVisibility: FloatingActionButtonVisibility.Always,
            onChangeWidgetDrawer: handleWidgetDrawerChanges,
            customActions: customActions,
            isBounded: false,
            maxColumns: (_b = (_a = effectiveBreakpoints['lg']) === null || _a === void 0 ? void 0 : _a.columns) !== null && _b !== void 0 ? _b : 30,
            widgetDrawerConfig: {
                defaultType: WidgetDrawerType.Widgets,
                showTypeFilter: false
            },
            onRegister: (controls) => {
                floatingActionsControlsRef.current = controls;
            }
        });
        return () => {
            unregisterFloatingActionSection('dashboard');
            floatingActionsControlsRef.current = null;
        };
    }, [isEditing, widgets, layouts, customActions, handleWidgetDrawerChanges, handleEnterEdit, handleExitEdit]);
    return _jsxs("div", { className: "uxpcore_dashboardview__container", children: [_jsx(Dashboard, { widgets: widgets, widgetPropsOverride: globalProps, layouts: layouts, onSave: handleSave, margin: effectiveSettings.margin != null ? [effectiveSettings.margin, effectiveSettings.margin] : undefined, padding: effectiveSettings.containerPadding != null ? [effectiveSettings.containerPadding, effectiveSettings.containerPadding] : undefined, isEditing: isEditing, allowFreePositioning: effectiveSettings.allowFreePositioning, showSpacerWidget: true, showGridlines: effectiveSettings.showGridlines, emptyPlaceholder: {
                    show: !isAnonymous && effectiveSettings.showEmptyPlaceholder,
                    onClick: () => { var _a; return (_a = floatingActionsControlsRef.current) === null || _a === void 0 ? void 0 : _a.openWidgetDrawer(); }
                }, breakpointOverride: (selectedBreakpoint === null || selectedBreakpoint === void 0 ? void 0 : selectedBreakpoint.key) || null, onContainerMount: handleContainerMount, breakpoints: effectiveBreakpoints }), _jsx(DashboardSettingsModal, { show: showSettingsModal, onClose: () => setShowSettingsModal(false), onSaveDefault: handleSaveDefaultDashboard, onClearWidgets: handleClearWidgets, onSaveGridSettings: handleSaveGridSettings, gridSettings: gridSettings, globalDashboardSettings: globalDashboardSettings, dashboardSpecificSettings: dashboardSpecificSettings, onSaveDashboardSpecificSettings: handleSaveDashboardSpecificSettings }), isEditing && (_jsx(DashboardConfigPanel, { containerRef: dashboardContainerRef, breakpointsSelection: {
                    enable: true,
                    selected: (selectedBreakpoint === null || selectedBreakpoint === void 0 ? void 0 : selectedBreakpoint.key) || null,
                    onSelect: handleBreakpointSelect,
                    breakpoints: effectiveBreakpoints
                }, userGroupsSelection: {
                    enable: false,
                    selected: null,
                    onSelect: () => { }
                }, dashboardSelection: {
                    enable: false,
                    ids: [],
                    selected: null,
                    onSelect: () => { }
                } }))] });
};
const DashboardView = memo((props) => (_jsx(ErrorBoundary, { children: _jsx(DashboardViewBase, Object.assign({}, props)) })));
export default DashboardView;
